import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/header";
import OrganizerBanner from "./components/organizerBanner.jsx";
import HomePage from "./pages/homePage.jsx";
import EventDetail from "./pages/eventDetail";
import RaceDetail from "./pages/raceDetail";
import CreateEvent from "./pages/createEvent";
import CreateRace from "./pages/createRace/createRace.jsx";
import Login from "./pages/login.jsx";
import Signup from "./pages/signup.jsx";
import SignupGoogle from "./pages/signupGoogle.jsx";
import User from "./pages/user.jsx";
import RaceRegistration from "./pages/raceRegistration/raceRegistration.jsx";
import Payment from "./pages/payment.jsx";
import Confirmation from "./pages/confirmation.jsx";
import AuthCallback from "./pages/callback.jsx";
import TicketDetail from "./pages/ticketDetail.jsx";
import EventsPage from "./pages/events.jsx";
import OrganizationDetail from "./pages/organizationDetail.jsx";
import Confidential from "./pages/confidential.jsx";
import Contact from "./pages/contact.jsx";
import About from "./pages/about.jsx";
import Debug from "./pages/debug.jsx";
import { API_URL } from "./utils/constants.js";
import Button from "./components/base/Button.jsx";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { theme } from "./theme.js";
import Footer from "./components/footer.jsx";

import "./App.css";

// Error Boundary to catch and suppress non-critical session lock errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    console.error("Error caught by boundary:", error?.message);
    // Never show error boundary - just let the app render
    return null;
  }

  componentDidCatch(error, errorInfo) {
    console.error("Caught error:", error?.message);
    console.error("Component stack:", errorInfo?.componentStack);
  }

  render() {
    // Always render children, never show error UI
    return this.props.children;
  }
}

function App() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    // Check localStorage for disclaimer seen status
    const disclaimerSeen = localStorage.getItem("disclaimerSeen");
    return !disclaimerSeen; // Show modal if not seen
  });

  useEffect(() => {
    // Save to localStorage when disclaimer is dismissed
    if (!showDisclaimer) {
      localStorage.setItem("disclaimerSeen", "true");
    }
  }, [showDisclaimer]);

  useEffect(() => {
    let isMounted = true;
    
    fetch(`${API_URL}/api/events`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (isMounted) {
          setEvents(data || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("API error:", err);
          setError(err.message || "Error fetching events");
          setLoading(false);
        }
      });
    
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ErrorBoundary>
      {/* Test Environment Disclaimer Modal */}
      {showDisclaimer && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(4px)",
        }}>
          <div style={{
            backgroundColor: theme.colors.bgLight,
            borderRadius: "12px",
            padding: "40px",
            maxWidth: "500px",
            boxShadow: theme.colors.shadow,
            border: `1px solid ${theme.colors.border}`,
            textAlign: "center"
          }}>
            <div style={{
              color: theme.colors.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <WrenchScrewdriverIcon height="60px" width="60px" />
            </div>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#0F172A",
              margin: "0 0 16px 0"
            }}>
              Environnement de Test
            </h2>
            <p style={{
              fontSize: "16px",
              color: "#6b7280",
              margin: "0 0 24px 0",
              lineHeight: "1.6",
              textAlign: "left",
            }}>
              Bienvenue ! Ceci est un environnement de démonstration. Toutes les données présentées ici sont simulées à des fins de test uniquement. Aucune transaction réelle ne sera traitée.
            </p>
            <Button
              onClick={() => setShowDisclaimer(false)}
              title="Compris"
            >
            </Button>
          </div>
        </div>
      )}

      <BrowserRouter>
        <div className="App" style={{ backgroundColor: theme.colors.bgLight}}>
          <OrganizerBanner />
          <Header />

          <Routes>
            <Route path="/" element={<HomePage events={events} loading={loading} error={error} />} />

            <Route path="/create-event" element={<CreateEvent />} />
            <Route path="/events/:event_id/races/:race_id" element={<RaceDetail />} />
            <Route path="/events/:event_id/races/:race_id/:price_id/register" element={<RaceRegistration />} />
            <Route path="/events/:event_id/races/:race_id/:price_id/payment" element={<Payment />} />
            <Route path="/payment/confirmation" element={<Confirmation />} />
            <Route path="/events/:event_id/create-race" element={<CreateRace />} />
            <Route path="/events/:event_id" element={<EventDetail events={events} />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/tickets/:ticket_id" element={<TicketDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signupGoogle" element={<SignupGoogle />} />
            <Route path="/user" element={<User />} />
            <Route path="/events" element={<EventsPage events={events} />} />
            <Route path="/organizers/:org_id" element={<OrganizationDetail />} />
            <Route path="/confidential" element={<Confidential />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/debug" element={<Debug />} />
          </Routes>
          <Footer />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;